import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {
  Aangifte,
  AangifteRapport,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity,
  Ondernemingsvorm
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, filter, Observable, shareReplay, take, tap} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-vermogensvergelijking',
    templateUrl: './vermogensvergelijking.component.html',
    styleUrls: ['./vermogensvergelijking.component.scss'],
    imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiAlertModule, CommonModule],
})
export class VermogensvergelijkingComponent implements OnInit {
    @Input() public id!: string;
    public dataService = inject(BokkzIbDataService);
    private bokkzClient = inject(IbRekenService);
    private destroyRef = inject(DestroyRef);
    public indexOnderneming = 0;
    public heeftOndernemingen = false;
    public heeftSamenwerkingsverband = false;
    public isPartner = false;
    public labelNaamOndereneming: any;
    public labelBalansOpening: any;
    public labelBalansSlot: any;
    public dataVisible$ = new BehaviorSubject<boolean>(false);

    public formGroup = new FormGroup({
        beginVermogen: new FormControl<number | null>(0),
        eindVermogen: new FormControl<number | null>(0),
        totaleOnttrekkingen: new FormControl<number | null>(0),
        totaleStortingen: new FormControl<number | null>(0),
        winstVolgensResultatenRekening: new FormControl<number | null>(0),
        aandeelInWinstVolgensResultatenRekening: new FormControl<number | null>(0),
        totaalActiefOpeningsbalans: new FormControl<number | null>(0),
        totaalPassiefOpeningsbalans: new FormControl<number | null>(0),
        totaalActiefSlotbalans: new FormControl<number | null>(0),
        totaalPassiefSlotbalans: new FormControl<number | null>(0),
        nietAftrekbareKostenEnLasten: new FormControl<number | null>(0),
        winstcorrectiesTotaal: new FormControl<number | null>(0),
        totaalPassiefSlotinvesteringsAftrekbalans: new FormControl<number | null>(0),
        investeringsBijtelling: new FormControl<number | null>(0),
        investeringsAftrek: new FormControl<number | null>(0),
        mutatieHIR: new FormControl<number | null>(0),
        mutatieKER: new FormControl<number | null>(0),
        mutatieFOR: new FormControl<number | null>(0),
        winstPerOnderneming: new FormControl<number | null>(0),
    });

    public tellingFG = new FormGroup({
        winstVolgensVergelijking: new FormControl<number | null>(0),
        verschilVermogen: new FormControl<number | null>(0),
        verschilWinst: new FormControl<number | null>(0),
        verschilOpeningsbalans: new FormControl<number | null>(0),
        verschilSlotbalans: new FormControl<number | null>(0),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly indexOnderneming$: Observable<number> = this.dataService.indexOnderneming$.pipe(
        tap((data) => {
            this.indexOnderneming = data;
        }),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    ngOnInit(): void {
        this.incomeTaxEntity$.subscribe();
        this.indexOnderneming$.subscribe();
        this.dataService.partnerAangifte$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.getRapportIfChanged(this.dataService.incomeTaxEntity.value.aangifte!, this.dataService.partnerAangifte.value);
        });
    }

    public getRapportIfChanged(aangifte: Aangifte, partner: boolean) {
        if (partner) {
            this.getLabels(aangifte.partnerAangifte!);
            this.bokkzClient.getIncomeTaxReport(aangifte.partnerAangifte!).subscribe((rapport) => {
                this.handleSwitch(rapport);
            });
        } else {
            this.getLabels(aangifte);
            this.bokkzClient.getIncomeTaxReport(aangifte).subscribe((rapport) => {
                this.handleSwitch(rapport);
            });
        }
    }

    private handleSwitch(details: AangifteRapport): void {
        this.resetAndPatchForms(details);
    }

    private resetAndPatchForms(details: AangifteRapport): void {
        this.formGroup.reset({}, { emitEvent: false });
        this.formGroup.patchValue(details.specificatieJaarrekening?.at(this.indexOnderneming)?.opbouw ?? {}, { emitEvent: false });
        this.formGroup.disable({ emitEvent: false });
        this.dataVisible$.next(true);
    }

    getLabels(data: Aangifte) {
        this.heeftOndernemingen = data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming) !== undefined;
        this.heeftSamenwerkingsverband =
            data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming)?.ondernemingsvorm !== Ondernemingsvorm.EENMANSZAAK;
        if (data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming) != null) {
            this.labelNaamOndereneming = data.box1.winstUitOnderneming.ondernemingen[this.indexOnderneming].naam!;
        }
        let begindatum;
        if (data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming)?.startOnderneming != null) {
            begindatum = data.box1.winstUitOnderneming.ondernemingen[this.indexOnderneming].startOnderneming!;
        } else {
            begindatum = new Date(data.aangifteJaar, 0, 1);
        }
        let slotdatum;
        if (data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming)?.datumStaking != null) {
            slotdatum = data.box1.winstUitOnderneming.ondernemingen[this.indexOnderneming].datumStaking!;
        } else {
            slotdatum = new Date(data.aangifteJaar, 11, 31);
        }
        this.labelBalansOpening =
            begindatum != null
                ? `${new Date(begindatum).toLocaleDateString('nl-NL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                  })}`
                : 'Openingsbalans';
        this.labelBalansSlot =
            slotdatum != null
                ? `${new Date(slotdatum).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                : 'Slotbalans';
    }

    calculateVerschilVermogen(): any {
        const eindVermogen = this.formGroup.get('eindVermogen')?.value ?? 0;
        const beginVermogen = this.formGroup.get('beginVermogen')?.value ?? 0;
        const diff = eindVermogen - beginVermogen;
        this.tellingFG.patchValue({ verschilVermogen: diff });
        return diff;
    }

    calculateWinstVolgensVergelijking(): any {
        const eindVermogen = this.formGroup.get('eindVermogen')?.value ?? 0;
        const beginVermogen = this.formGroup.get('beginVermogen')?.value ?? 0;
        const diff = eindVermogen - beginVermogen;
        const onttrekkingen = this.formGroup.get('totaleOnttrekkingen')?.value ?? 0;
        const stortingen = this.formGroup.get('totaleStortingen')?.value ?? 0;
        const calc = diff + onttrekkingen - stortingen;
        this.tellingFG.patchValue({ winstVolgensVergelijking: calc });
        return calc;
    }

    calculateVerschilWinst(): any {
        let verschilWinst = 0;
        const opbouw = this.formGroup;
        const winstVolgensResRek = opbouw?.get('winstVolgensResultatenRekening')?.value ?? 0;
        const aandeelInWinstVolgensResultatenRekening = opbouw?.get('aandeelInWinstVolgensResultatenRekening')?.value ?? 0;
        const winstVolgensVergelijking = this.tellingFG.get('winstVolgensVergelijking');
        if (opbouw && winstVolgensVergelijking) {
            const verschil = winstVolgensVergelijking.value ?? 0;

            if (!this.heeftSamenwerkingsverband) {
                verschilWinst = Math.abs(verschil - winstVolgensResRek);
            } else {
                verschilWinst = Math.abs(verschil - aandeelInWinstVolgensResultatenRekening);
            }
        }
        this.tellingFG.patchValue({ verschilWinst: verschilWinst });
        return verschilWinst;
    }
}
